import React from 'react'
import { useNode, Node } from '@craftjs/core'
import clsx from 'clsx'
import { capitalize } from 'lodash'
import { Form, Input, Switch } from '@pankod/refine-antd'
import {
  RadioInput,
  RadioInputGroup as LibraryRadio,
} from '@instructure/ui-radio-input'
import { Controller } from 'react-hook-form'

import {
  RadioGroupCustomProps,
  TRadioGroup,
  WIDGET_NAME,
} from '@components/FormBuilder/schema'
import {
  mappedWidgetSettingsError,
  widgetSettingsOnValueChange,
  getWidgetSettingsFields,
} from '@components/FormBuilder/utils'
import { useFormContext } from '@components/FormBuilder/formContext'
import MenuOptions, {
  defaultOptions,
  optionsParser,
} from '../utils/MenuOptions'
import { useGetFormType } from '@components/FormBuilder/editor/toolboxWidgets/utils'
import ContentForm from '@components/ContentBank/ContentForm'

export const defaultProps: TRadioGroup = {
  label: 'Radio label',
  name: 'radioName',
  options: defaultOptions,
}

const RadioGroup = ({ name, label, helper, options }: TRadioGroup) => {
  const { control } = useFormContext()

  const {
    connectors: { connect, drag },
  } = useNode()
  return (
    <div
      ref={(ref) => connect(drag(ref!))}
      className={clsx(
        'mb-3 last:mb-0',
        'widget-component',
        'widget-component--checkbox-group',
        'text-default',
      )}
    >
      <Controller
        name={name}
        control={control}
        render={({
          field: { name, onChange, value },
          fieldState: { error },
        }) => (
          <LibraryRadio
            onChange={onChange}
            description={
              <div
                className="form-label ql-editor"
                dangerouslySetInnerHTML={{ __html: label }}
              />
            }
            name={name}
            value={value}
            messages={
              error?.message
                ? [{ text: capitalize(error.message), type: 'error' }]
                : [{ text: helper || null, type: 'hint' }]
            }
          >
            {optionsParser(options).map((v, i) => (
              <RadioInput
                key={`radio-options-${i}`}
                label={v.label}
                value={v.value}
              />
            ))}
          </LibraryRadio>
        )}
      />
    </div>
  )
}

const RadioGroupSettings = () => {
  const { formType } = useGetFormType()
  const {
    actions: { setProp, setCustom },
    props,
    custom,
  } = useNode((node) => ({
    props: node.data.props,
    custom: node.data.custom,
  }))
  const errors = mappedWidgetSettingsError(custom.errors)
  return (
    <Form
      onValuesChange={(changedValue) =>
        widgetSettingsOnValueChange(changedValue, custom, setCustom)
      }
      fields={getWidgetSettingsFields(props)}
      layout="vertical"
    >
      <Form.Item
        label="Name Key"
        name="name"
        validateStatus={errors['name'] && 'error'}
        help={errors['name']}
      >
        <Input
          autoComplete="off"
          value={props.name}
          onChange={(e) =>
            setProp(
              (props: TRadioGroup) =>
                (props.name = e.target.value.replaceAll(' ', '')),
            )
          }
        />
      </Form.Item>
      <Form.Item
        label="Label"
        name="label"
        validateStatus={errors['label'] && 'error'}
        help={errors['label']}
      >
        <ContentForm
          index="widgetText"
          showDeleteButton={false}
          showIndentation={false}
          showUploader={false}
          showTitle={false}
          value={props.text}
          onChange={(text) =>
            setProp((props: TRadioGroup) => (props.label = text))
          }
        />
      </Form.Item>
      <Form.Item
        label="Options"
        name="options"
        validateStatus={errors['options'] && 'error'}
        help={errors['options']}
      >
        <MenuOptions
          onChange={(e) => setProp((props: TRadioGroup) => (props.options = e))}
          numberOnly={formType === 'quantitative'}
        />
      </Form.Item>
      <Form.Item
        label="Required"
        name="isRequired"
        validateStatus={errors['isRequired'] && 'error'}
        help={errors['isRequired']}
      >
        <Switch
          checked={props.isRequired}
          onChange={(e) =>
            setProp((props: TRadioGroup) => (props.isRequired = e))
          }
        />
      </Form.Item>
      <Form.Item
        label="Helper Text"
        name="helper"
        validateStatus={errors['helper'] && 'error'}
        help={errors['helper']}
      >
        <Input
          autoComplete="off"
          value={props.helper}
          onChange={(e) =>
            setProp((props: TRadioGroup) => (props.helper = e.target.value))
          }
        />
      </Form.Item>
    </Form>
  )
}

RadioGroup.craft = {
  rules: {
    canDrop: (targetNode: Node) => {
      return targetNode.data.name === WIDGET_NAME.Container
    },
  },
  props: defaultProps,
  custom: RadioGroupCustomProps,
  related: {
    settings: RadioGroupSettings,
  },
}

export { RadioGroup }
